import { ApiError } from '@lib/network/api';
import { userStore } from '@stores/User';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

// Mirrors the backend's scope catalogue (GET /user/api-keys/scopes) by hand: a scope the
// backend adds shows up here only once this file is updated to match.
export type ApiScope =
  | 'profile:read'
  | 'billing:read'
  | 'billing:consume'
  | 'library:read'
  | 'library:write'
  | 'library:delete'
  | 'library:search'
  | 'rendering:read'
  | 'rendering:write'
  | 'social:read'
  | 'social:publish';

export const API_SCOPES: ApiScope[] = [
  'profile:read',
  'billing:read',
  'billing:consume',
  'library:read',
  'library:write',
  'library:delete',
  'library:search',
  'rendering:read',
  'rendering:write',
  'social:read',
  'social:publish',
];

/** Every scope, grouped for the picker. Hiding a scope from the picker goes in HIDDEN_SCOPES. */
export const SCOPE_GROUPS: { label: string; scopes: ApiScope[] }[] = [
  { label: 'Profile', scopes: ['profile:read'] },
  { label: 'Billing', scopes: ['billing:read', 'billing:consume'] },
  {
    label: 'Library',
    scopes: ['library:read', 'library:write', 'library:delete', 'library:search'],
  },
  { label: 'Rendering', scopes: ['rendering:read', 'rendering:write'] },
  { label: 'Social', scopes: ['social:read', 'social:publish'] },
];

/**
 * Scopes the Create API Key picker leaves out on purpose. They stay in ApiScope and the
 * label map: keys created earlier still carry them, and the key row lists what a key has.
 *
 * FIXME: billing:consume is hidden here while the backend still offers it from
 * /user/api-keys/scopes. Acceptable once, as a frontend-only hide. Retire this entry (and the
 * set if it empties) once the backend stops returning the scope.
 */
export const HIDDEN_SCOPES: ReadonlySet<ApiScope> = new Set<ApiScope>(['billing:consume']);

export const SCOPE_LABELS: Record<ApiScope, string> = {
  'profile:read': 'Read profile',
  'billing:read': 'Read billing',
  'billing:consume': 'Consume credits',
  'library:read': 'Read library',
  'library:write': 'Write library',
  'library:delete': 'Delete library',
  'library:search': 'Search library',
  'rendering:read': 'Read rendering',
  'rendering:write': 'Write rendering',
  'social:read': 'Read social accounts',
  'social:publish': 'Publish to social',
};

/** The backend's own wording for each scope, shown as the picker chip's tooltip. */
export const SCOPE_DESCRIPTIONS: Record<ApiScope, string> = {
  'profile:read': 'Read your basic profile information',
  'billing:read': 'Read your billing info and credit amounts',
  'billing:consume': 'Take actions that require spending credits on your behalf',
  'library:read': 'Read your libraries and their content',
  'library:write': 'Create libraries and add media to them',
  'library:delete': 'Delete libraries and media',
  'library:search': 'Search through your content',
  'rendering:read': 'Read and retrieve past rendered videos',
  'rendering:write': 'Create and run new render jobs, and preview single frames of one',
  'social:read': 'See your linked social accounts and the posts scheduled to them',
  'social:publish': 'Schedule, edit and cancel posts to your linked social accounts',
};

export interface SanitizedApiKey {
  _id: string;
  key: string;
  name: string;
  userId: string;
  billingId: string;
  scopes: ApiScope[];
  createdAt: number;
  updatedAt: number;
}

export class ApiKeysStore {
  @observable
  keys: SanitizedApiKey[] = [];

  @observable
  fetching = false;

  @observable
  error: string | null = null;

  constructor() {
    makeObservable(this);
  }

  @computed
  get sortedKeys() {
    return [...this.keys].sort((a, b) => b.createdAt - a.createdAt);
  }

  @action
  async fetchKeys() {
    this.fetching = true;
    this.error = null;
    try {
      const data = await userStore.apiWithAuth<SanitizedApiKey[]>('/user/api-keys');
      runInAction(() => {
        this.keys = data;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof ApiError ? e.message : 'Failed to fetch API keys';
      });
    } finally {
      runInAction(() => {
        this.fetching = false;
      });
    }
  }

  @action
  async createKey(name: string, scopes: ApiScope[]): Promise<SanitizedApiKey> {
    const data = await userStore.apiWithAuth<SanitizedApiKey>('/user/api-keys', {
      method: 'POST',
      body: { name, scopes },
    });
    runInAction(() => {
      this.keys.push({ ...data, key: this.truncateKey(data.key) });
    });
    return data;
  }

  @action
  async rotateKey(keyId: string): Promise<SanitizedApiKey> {
    const data = await userStore.apiWithAuth<SanitizedApiKey>(`/user/api-keys/${keyId}`, {
      method: 'POST',
    });
    runInAction(() => {
      const idx = this.keys.findIndex((k) => k._id === keyId);
      if (idx !== -1) {
        this.keys[idx] = { ...data, key: this.truncateKey(data.key) };
      }
    });
    return data;
  }

  @action
  async deleteKey(keyId: string) {
    await userStore.apiWithAuth(`/user/api-keys/${keyId}`, {
      method: 'DELETE',
    });
    runInAction(() => {
      this.keys = this.keys.filter((k) => k._id !== keyId);
    });
  }

  private truncateKey(key: string): string {
    // If already truncated, return as-is
    if (key.includes('...')) return key;
    const prefix = key.slice(0, 14); // "sk_sz_" + 8 hex chars
    const suffix = key.slice(-4);
    return `${prefix}...${suffix}`;
  }
}

export const apiKeysStore = new ApiKeysStore();
